import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";

// Encodes form data the way Netlify Forms expects it
const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    practiceName: "",
    specialty: "",
    monthlyClaimVolume: "",
    email: "",
    phone: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.specialty) {
      toast.error("Please select your specialty.");
      return;
    }
    if (!formData.monthlyClaimVolume) {
      toast.error("Please select your monthly claim volume.");
      return;
    }

    setIsSubmitting(true);

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...formData }),
      });
      toast.success("Thank you! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        practiceName: "",
        specialty: "",
        monthlyClaimVolume: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      const errorMsg =
        "Failed to submit. Please try again or call us directly at (800) 555-1234.";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-32 bg-gradient-to-br from-slate-50 to-white"
    >
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-[#14b8a6] font-semibold text-sm tracking-wider uppercase mb-4">
            Get Started Today
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Let's Grow Your Practice Revenue
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Schedule your free billing analysis and discover how much revenue
            you're leaving on the table.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>

              <div>
                <Label
                  htmlFor="name"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Your Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3 transition-all duration-200"
                  placeholder="Dr. Jane Smith"
                />
              </div>

              <div>
                <Label
                  htmlFor="practiceName"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Practice Name *
                </Label>
                <Input
                  id="practiceName"
                  name="practiceName"
                  type="text"
                  required
                  value={formData.practiceName}
                  onChange={handleChange}
                  className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3 transition-all duration-200"
                  placeholder="Your Practice Name"
                />
              </div>

              <div>
                <Label
                  htmlFor="specialty"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Specialty *
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleSelectChange("specialty", value)
                  }
                  value={formData.specialty}
                >
                  <SelectTrigger className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3">
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">Family Practice</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="mental-health">
                      Behavioral/Mental Health
                    </SelectItem>
                    <SelectItem value="physical-therapy">
                      Physical Therapy
                    </SelectItem>
                    <SelectItem value="dermatology">Dermatology</SelectItem>
                    <SelectItem value="orthopedics">Orthopedics</SelectItem>
                    <SelectItem value="chiropractic">Chiropractic</SelectItem>
                    <SelectItem value="urgent-care">Urgent Care</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="monthlyClaimVolume"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Monthly Claim Volume *
                </Label>
                <Select
                  onValueChange={(value) =>
                    handleSelectChange("monthlyClaimVolume", value)
                  }
                  value={formData.monthlyClaimVolume}
                >
                  <SelectTrigger className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3">
                    <SelectValue placeholder="Select volume" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-500">0-500 claims</SelectItem>
                    <SelectItem value="500-1000">500-1,000 claims</SelectItem>
                    <SelectItem value="1000-2500">
                      1,000-2,500 claims
                    </SelectItem>
                    <SelectItem value="2500+">2,500+ claims</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label
                  htmlFor="email"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3 transition-all duration-200"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <Label
                  htmlFor="phone"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Phone *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3 transition-all duration-200"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <Label
                  htmlFor="message"
                  className="text-slate-700 font-medium mb-2 block"
                >
                  Message (Optional)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="border-2 border-slate-200 focus:border-[#14b8a6] rounded-lg p-3 transition-all duration-200"
                  placeholder="Tell us about your practice and billing needs..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-gradient-to-r from-[#1e3a8a] to-[#14b8a6] hover:from-[#1e3a8a]/90 hover:to-[#14b8a6]/90 text-white py-6 text-lg rounded-full group transition-all duration-300 hover:shadow-2xl hover:shadow-[#1e3a8a]/30"
              >
                {isSubmitting ? "Sending..." : "Get Your Free Analysis"}
                <Send
                  className="ml-2 group-hover:translate-x-1 transition-transform"
                  size={20}
                />
              </Button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#0f2557] text-white rounded-2xl p-10 h-full flex flex-col justify-center">
              <h3 className="text-3xl font-bold mb-8">Get in Touch</h3>

              <div className="space-y-6">
                <motion.div
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#14b8a6]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#14b8a6]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Phone</div>
                    <a
                      href="tel:+18005551234"
                      className="text-slate-200 hover:text-[#14b8a6] transition-colors"
                    >
                      +1 571-208-2269
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#14b8a6]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#14b8a6]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Email</div>
                    <a
                      href="mailto:info@billwellbenefits.com"
                      className="text-slate-200 hover:text-[#14b8a6] transition-colors"
                    >
                      info@billwellbenefits.com
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#14b8a6]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#14b8a6]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Office</div>
                    <p className="text-slate-200">
                      Unit 03, South Annex Suite 25 140 E Broadway Avenue Jackson, WY 83001
                    </p>
                  </div>
                </motion.div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/20">
                <p className="text-slate-300 text-sm">
                  We typically respond within 24 hours. For urgent billing
                  issues, please call us directly.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
